import React from 'react';

import { useIntl } from 'react-intl';

import PropTypes from 'prop-types';

import BoxContainer from '@src/components/BoxContainer';
import Title from 'components/BoxContainer/components/Title';
import FilterContainer from '@src/components/FilterContainer';

import { ALLOWED_ROLES } from '@src/packages/utils';

const DashboardHeader = ({
  boardSelected,
  hasBoardSelected,
  isMobile,
  savingLayoutChanges,
  creatingChart,
  handleDeleteBoard,
  onOpenBoardFormEdit,
  onCreateChart,
  saveLayout,
  sideBarIsOpen,
  toggleSideBar,
  hasChanges,
}) => {
  const intl = useIntl();

  // A board belongs either to the org or to a single division; only that scope
  // may edit it. Without divId, ComponentBlocker matches any assigned division.
  const boardDivId = boardSelected?.divId ?? undefined;
  const boardRoles = boardDivId
    ? ALLOWED_ROLES.ORGANIZATIONS.DIVISIONS.DASHBOARDS
    : ALLOWED_ROLES.ORGANIZATIONS.DASHBOARDS;

  return (
    <BoxContainer fullContent fixed bottomBorder>
      <FilterContainer
        title={<Title.Header value={boardSelected?.name || '-'} />}
        actionButtons={[
          {
            label: intl.formatMessage({ id: 'dashboard.tooltip.deleteBoard' }),
            iconName: 'delete',
            type: 'secondary',
            action: handleDeleteBoard,
            disabled: !hasBoardSelected,
            allowedRole: boardRoles.DELETE,
            divId: boardDivId,
            tooltipText: intl.formatMessage({ id: 'dashboard.tooltip.deleteBoard' }),
          },
          {
            label: intl.formatMessage({ id: 'dashboard.tooltip.editBoard' }),
            iconName: 'edit',
            type: 'secondary',
            action: onOpenBoardFormEdit,
            disabled: !hasBoardSelected,
            allowedRole: boardRoles.UPDATE,
            divId: boardDivId,
            tooltipText: intl.formatMessage({ id: 'dashboard.tooltip.editBoard' }),
          },
          {
            label: intl.formatMessage({
              id: sideBarIsOpen ? 'filterDropdown.closeFilters' : 'dashboard.tooltip.filter',
            }),
            iconName: 'filter_alt',
            type: 'secondary',
            action: toggleSideBar,
            className: sideBarIsOpen ? 'gx-border-primary' : '',
          },
          {
            label: intl.formatMessage({ id: 'dashboard.tooltip.createChart' }),
            iconName: 'add',
            type: 'primary',
            action: onCreateChart,
            hidden: !isMobile && creatingChart,
            disabled: !hasBoardSelected,
            allowedRole: boardRoles.CHARTS.CREATE,
            divId: boardDivId,
            tooltipText: intl.formatMessage({ id: 'dashboard.tooltip.createChart' }),
          },
          {
            label: intl.formatMessage({ id: 'dashboard.tooltip.saveBoard' }),
            iconName: 'save',
            type: 'primary',
            action: saveLayout,
            loading: savingLayoutChanges,
            hidden: !hasChanges,
            disabled: !hasBoardSelected,
            allowedRole: boardRoles.CHARTS.UPDATE,
            divId: boardDivId,
            tooltipText: intl.formatMessage({ id: 'dashboard.tooltip.saveBoard' }),
          },
        ]}
      />
    </BoxContainer>
  );
};

DashboardHeader.propTypes = {
  boardSelected: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    divId: PropTypes.string,
  }).isRequired,
  hasBoardSelected: PropTypes.bool.isRequired,
  savingLayoutChanges: PropTypes.bool.isRequired,
  creatingChart: PropTypes.bool.isRequired,
  isMobile: PropTypes.bool.isRequired,
  handleDeleteBoard: PropTypes.func.isRequired,
  onOpenBoardFormEdit: PropTypes.func.isRequired,
  onCreateChart: PropTypes.func.isRequired,
  saveLayout: PropTypes.func.isRequired,
  sideBarIsOpen: PropTypes.bool.isRequired,
  toggleSideBar: PropTypes.func.isRequired,
  hasChanges: PropTypes.bool.isRequired,
};

export default React.memo(DashboardHeader);
